/**
 * Shared 403 toast interceptor for Axios clients.
 *
 * Installs a response interceptor that displays a single toast when the API
 * returns a 403, with messages specialized for the modular access control
 * codes (US-MAC) :
 *   - `NO_SUBSCRIPTION` : la brand n'a pas souscrit ce module
 *   - `NO_MODULE_ROLE` : l'user n'a pas de role pour ce module
 *   - `USER_BRAND_NOT_FOUND` : l'user n'est pas membre de cette brand
 *
 * Toutes les notifications partagent le **meme `toast.id`**, ce qui evite le
 * spam quand plusieurs requetes echouent en parallele (page qui charge 5
 * endpoints qui retournent tous 403). `react-hot-toast` met a jour le toast
 * existant au lieu d'en creer un nouveau.
 *
 * Usage :
 * ```ts
 * import { installForbiddenToastInterceptor } from '@bill/ui';
 * installForbiddenToastInterceptor({ client: apiClient });
 * installForbiddenToastInterceptor({ client: coreApiClient });
 * ```
 */

import type { AxiosError, AxiosInstance } from 'axios';
import toast from 'react-hot-toast';

const TOAST_ID = 'forbidden-403';

const CODE_MESSAGES: Record<string, string> = {
  NO_SUBSCRIPTION: "Ce module n'est pas active sur cet etablissement.",
  NO_MODULE_ROLE: "Vous n'avez pas acces a ce module sur cet etablissement.",
  USER_BRAND_NOT_FOUND: "Vous n'etes pas membre de cet etablissement.",
  NO_BRAND_CONTEXT: 'Aucun etablissement actif.',
};

export interface InstallForbiddenToastOptions {
  client: AxiosInstance;
  /** Message affiche pour les 403 sans code specifique. */
  defaultMessage?: string;
}

export function installForbiddenToastInterceptor(
  options: InstallForbiddenToastOptions,
): void {
  const {
    client,
    defaultMessage = "Vous n'avez pas la permission d'effectuer cette action.",
  } = options;

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ error?: { code?: string; message?: string } }>) => {
      if (error.response?.status === 403) {
        const code = error.response.data?.error?.code;
        const message = (code && CODE_MESSAGES[code]) ?? defaultMessage;
        toast.error(message, { id: TOAST_ID });
      }
      return Promise.reject(error);
    },
  );
}
